import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/Spinner";
import { X, Camera, ScanLine, Keyboard, Check } from "lucide-react";
import type { TrailerData } from "@/store/portal-store";

interface AddTrailerModalProps {
  onAdd: (trailer: TrailerData) => void;
  onClose: () => void;
}

const PHOTO_SLOTS = ["Front", "Rear", "Left Side", "Right Side"] as const;

export function AddTrailerModal({ onAdd, onClose }: AddTrailerModalProps) {
  const [mode, setMode] = useState<"camera" | "manual">("camera");
  const [vin, setVin] = useState("");
  const [acv, setAcv] = useState("");
  const [decoded, setDecoded] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [photos, setPhotos] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const trailerInfo = {
    year: "2021",
    make: "Great Dane",
    model: "Champion SE",
    trailerType: "Dry Van",
  };

  const vinValid = /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
  const allPhotos = PHOTO_SLOTS.every((s) => photos[s]);
  const acvValid = acv.length > 0 && parseFloat(acv.replace(/[^0-9.]/g, "")) > 0;
  const canSubmit = decoded && acvValid && allPhotos;

  const handleScan = () => {
    setDecoding(true);
    setTimeout(() => {
      setVin("1GRAA0628DB706123");
      setDecoded(true);
      setDecoding(false);
    }, 2000);
  };

  const handleDecode = () => {
    if (vinValid) {
      setDecoding(true);
      setTimeout(() => {
        setDecoded(true);
        setDecoding(false);
      }, 1500);
    }
  };

  const capturePhoto = (slot: string) => {
    setPhotos((p) => ({ ...p, [slot]: true }));
  };

  const handleAdd = () => {
    setSaving(true);
    setTimeout(() => {
      onAdd({
        vin,
        ...trailerInfo,
        acv: acv.replace(/[^0-9]/g, ""),
        photos: Object.keys(photos),
      });
    }, 1500);
  };

  const formatCurrency = (value: string) => {
    const num = value.replace(/[^0-9]/g, "");
    if (!num) return "";
    return new Intl.NumberFormat("en-US").format(parseInt(num));
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h2 className="text-lg font-bold">Add a Trailer</h2>
        <button type="button" onClick={onClose}>
          <X className="w-6 h-6 text-gray-400" />
        </button>
      </div>

      <div className="px-6 py-6 space-y-6">
        {!decoded && (
          <>
            {mode === "camera" ? (
              <>
                <div
                  className="bg-gray-900 rounded-2xl h-40 flex flex-col items-center justify-center cursor-pointer"
                  onClick={handleScan}
                >
                  {decoding ? (
                    <Spinner size="md" className="border-white border-t-blue-400" />
                  ) : (
                    <>
                      <ScanLine className="w-10 h-10 text-white/60 mb-2" />
                      <p className="text-white/60 text-sm">Tap to scan trailer VIN</p>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setMode("manual")}
                  className="flex items-center justify-center gap-2 text-sm text-blue-500 w-full"
                >
                  <Keyboard className="w-4 h-4" /> Enter VIN Manually
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <Label>VIN (17 characters)</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter 17-character VIN"
                    value={vin}
                    onChange={(e) =>
                      setVin(
                        e.target.value
                          .toUpperCase()
                          .replace(/[^A-HJ-NPR-Z0-9]/gi, "")
                          .slice(0, 17)
                      )
                    }
                    className="h-12 font-mono"
                  />
                  <Button onClick={handleDecode} disabled={!vinValid || decoding} className="h-12">
                    {decoding ? <Spinner size="sm" /> : "Decode"}
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => setMode("camera")}
                  className="text-sm text-blue-500 flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" /> Use Camera
                </button>
              </div>
            )}
          </>
        )}

        {decoded && (
          <>
            <Card>
              <CardContent className="pt-4 space-y-2">
                <p className="text-xs text-gray-400 font-mono">{vin}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-400">Year</span>
                    <p className="font-medium">{trailerInfo.year}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Make</span>
                    <p className="font-medium">{trailerInfo.make}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Model</span>
                    <p className="font-medium">{trailerInfo.model}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Type</span>
                    <p className="font-medium">{trailerInfo.trailerType}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Trailer Actual Cash Value</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="$"
                value={acv ? `$${formatCurrency(acv)}` : ""}
                onChange={(e) => setAcv(e.target.value.replace(/[^0-9]/g, ""))}
                className="h-12 text-base"
              />
            </div>

            <div>
              <Label className="mb-3 block">Photos (4 required)</Label>
              <div className="grid grid-cols-2 gap-3">
                {PHOTO_SLOTS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => capturePhoto(slot)}
                    className={`h-24 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all ${
                      photos[slot]
                        ? "border-green-400 bg-green-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    {photos[slot] ? (
                      <Check className="w-6 h-6 text-green-500" />
                    ) : (
                      <Camera className="w-6 h-6 text-gray-400" />
                    )}
                    <span className="text-xs text-gray-500">{slot}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <Button
          className="w-full h-12 text-base"
          disabled={!canSubmit || saving}
          onClick={handleAdd}
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" className="border-white border-t-transparent" />
              Updating your quote...
            </span>
          ) : (
            "Add Trailer"
          )}
        </Button>
      </div>
    </div>
  );
}
